<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getRequisitionStatisticsAction } from '@/api/functions/requisitions/get-requisition-statistics.action'
import { useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useRequisitions } from '@/composables/useRequisitions'
import RequestCard from './partials/RequestCard.vue'
import RequisitionOverview from './partials/RequisitionOverview.vue'

const router = useRouter()
const route = useRoute()

// This tab always shows requisitions that have a cancellation request awaiting review.
// The filter is fixed and cannot be changed by the filter form or the URL.
const FIXED_FILTERS = { having_cancellation: true }

//  Fetch requisition statistics
const { data } = useQueryApi({
  queryKey: ['requisitionsStatistics'],
  queryFn: getRequisitionStatisticsAction,
  staleTime: 1000 * 60 * 5,
})

const { statusOptions, discountOptions, currencies, countries, transferMethods } = useGeneralSettings()

//  Map API data by slug for easier access
const stats = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map: Record<string, any> = {}
  if (data.value?.data?.length) {
    for (const item of data.value.data) {
      map[item.slug] = item
    }
  }
  return map
})

//  Define main requisition metrics to display in dashboard cards
const requisitionsStatus = computed(() => [
  {
    label: 'تعداد درخواست‌های ثبت شده (ماه جاری)',
    price: stats.value.total_requisitions?.value ?? 0,
    currency: 'COUNT',
  },
  {
    label: 'جمع مقدار ریالی درخواست‌های ثبت شده (ماه جاری)',
    price: stats.value.sum_rial_amount_requisitions?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'جمع حجم ارزی درخواست‌های ثبت شده (ماه جاری)',
    price: stats.value.sum_currency_amount_requisitions?.value ?? 0,
    currency: 'TOMAN',
  },
])

//  Tabs navigation for requisition types
const tabs = [
  { title: 'همه درخواست‌ها', link: '/requisitions' },
  { title: 'درخواست‌های خرید', link: '/requisitions/buy' },
  { title: 'درخواست‌های فروش', link: '/requisitions/sell' },
  { title: 'درخواست‌های لغو', link: '/requisitions/cancellation-requests' },
  { title: 'درخواست‌های لغو شده', link: '/requisitions/cancelled' },
]

//  Filter configuration for search and refinement
const filterFields = computed(() => [
  {
    type: 'input',
    key: 'code',
    label: 'شماره درخواست را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'range',
    key: 'requestAmount',
    title: 'حجم درخواست',
    rangeKeys: {
      min: 'from_rial_amount',
      max: 'till_rial_amount',
    },
  },
  {
    type: 'range',
    key: 'exchangeRate',
    title: 'نرخ تبدیل',
    rangeKeys: {
      min: 'from_exchange_rate',
      max: 'till_exchange_rate',
    },
  },
  {
    type: 'select',
    key: 'currency',
    title: 'نوع ارز',
    options: currencies.value,
  },
  {
    type: 'select',
    key: 'status',
    label: 'انتخاب وضعیت درخواست',
    title: 'وضعیت درخواست',
    options: statusOptions.value,
  },
  {
    type: 'select',
    key: 'discount',
    label: 'انتخاب تخفیف',
    title: 'تخفیف',
    options: discountOptions.value,
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
  {},
  {
    type: 'checkbox',
    key: 'offer_type',
    label: 'درخواست‌های دارای پیشنهاد بی‌پاسخ',
  },
])

// --- Filters Events ---
const onApplyFilters = (values: Record<string, any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: Record<string, any> = { ...values, ...FIXED_FILTERS }

  if (payload.offer_type === true) {
    payload.offer_type = 'PENDING'
  } else {
    delete payload.offer_type
  }

  updateFilters(payload)

  const cleanQuery = Object.fromEntries(
    Object.entries(payload).filter(
      ([key, value]) => key !== 'having_cancellation' && value !== '' && value !== null && value !== undefined
    )
  )

  router.push({
    path: route.path,
    query: cleanQuery,
  })
}

const { requisitions, isLoading, pagination, currentPage, updateFilters, goToPage, refetchRequisitions } =
  useRequisitions({
    ...FIXED_FILTERS,
    page: 1,
  })

function resetFiltersAndReload() {
  updateFilters({ ...FIXED_FILTERS })
  goToPage(1)
  router.push({ path: route.path, query: {} })
}

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    // having_cancellation from the URL is ignored on purpose
    updateFilters({ ...filters, ...FIXED_FILTERS })

    if (page) {
      const pageNum = Number(page)

      if (!isNaN(pageNum)) {
        currentPage.value = pageNum
      }
    }
  }
})
</script>

<template>
  <div class="w-full mb-12">

    <!-- 👉  Overview section with stats and tabs -->
    <RequisitionOverview :stats="requisitionsStatus" :tabs="tabs" title="وضعیت درخواست‌ها" :countries="countries"
      :currencies="currencies" :discount-options="discountOptions" :transfer-methods="transferMethods" />

    <!-- 👉 Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" class="mt-20 lg:mt-8" />

    <!-- 👉 Requests list and pagination -->
    <div v-if="isLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات...
    </div>
    <div v-else>
      <div v-if="requisitions.length > 0" class="mt-6 flex flex-col gap-6 ">
        <RequestCard v-for="(request, index) in requisitions" :key="`card-item-${index}`" :request="request"
          cancellation-review @deleted="refetchRequisitions" @decided="refetchRequisitions" />

        <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage"
          :totalItems="pagination?.total" @update:currentPage="goToPage" :isAdmin="true" />

      </div>
      <EmptyState v-else :onReset="resetFiltersAndReload" />
    </div>

  </div>
</template>
